import { InfoIcon } from '@chakra-ui/icons';
import {
  Box,
  Divider,
  Heading,
  List,
  ListIcon,
  ListItem,
  Text,
  VStack,
} from '@chakra-ui/react';
import React from 'react';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <Box maxW="4xl" mx="auto" p={6}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Box>
          <Heading size="lg" mb={2}>
            Privacy Policy
          </Heading>
          <Text color="gray.600">
            Last updated: {new Date().toLocaleDateString()}
          </Text>
        </Box>

        {/* Introduction */}
        <Box>
          <Heading size="md" mb={3}>
            1. Simple Privacy Promise
          </Heading>
          <Text>
            We keep your data private and simple. Your settings are
            saved on your computer (not on our servers), so you can
            clear your browser history and cookies anytime without
            losing access to any features.
          </Text>
        </Box>

        {/* Information We Collect */}
        <Box>
          <Heading size="md" mb={3}>
            2. What We Save on Your Computer
          </Heading>

          <Heading size="sm" mb={2}>
            2.1 Your Settings
          </Heading>
          <Text mb={3}>
            We save these things on your computer only (not on our
            servers):
          </Text>
          <List spacing={2} ml={4}>
            <ListItem>
              <ListIcon as={InfoIcon} color="blue.500" />
              Your app preferences and settings
            </ListItem>
            <ListItem>
              <ListIcon as={InfoIcon} color="blue.500" />
              Which features you've turned on or off
            </ListItem>
          </List>
          <Text
            mt={3}
            p={3}
            bg="green.50"
            borderRadius="md"
            borderLeft="4px solid"
            borderLeftColor="green.400"
          >
            <strong>
              Good news for teams that clear browser history:
            </strong>{' '}
            Your advanced features (like AI Classification) are
            controlled by your user role, not by saved settings. Even
            if you clear your browser daily/weekly, you'll keep access
            to all your features!
          </Text>

          <Heading size="sm" mb={2} mt={4}>
            2.2 AI Features (When You Use Them)
          </Heading>
          <Text mb={3}>
            If you use AI features, we collect basic usage info to
            make them better:
          </Text>
          <List spacing={2} ml={4}>
            <ListItem>
              <ListIcon as={InfoIcon} color="blue.500" />
              How often you agree or disagree with AI suggestions
            </ListItem>
            <ListItem>
              <ListIcon as={InfoIcon} color="blue.500" />
              How fast the AI responds
            </ListItem>
          </List>
        </Box>

        {/* How We Use Information */}
        <Box>
          <Heading size="md" mb={3}>
            3. How We Use This Information
          </Heading>
          <Text mb={3}>We only use your info to:</Text>
          <List spacing={2} ml={4}>
            <ListItem>
              <ListIcon as={InfoIcon} color="blue.500" />
              Remember your settings so the app works how you like it
            </ListItem>
            <ListItem>
              <ListIcon as={InfoIcon} color="blue.500" />
              Make the AI features smarter (if you use them)
            </ListItem>
            <ListItem>
              <ListIcon as={InfoIcon} color="blue.500" />
              Keep the app running smoothly
            </ListItem>
          </List>
        </Box>

        {/* Data Storage */}
        <Box>
          <Heading size="md" mb={3}>
            4. Where Your Data Lives
          </Heading>
          <Text mb={3}>
            Your settings are saved on your computer only. Here's what
            that means:
          </Text>
          <List spacing={2} ml={4}>
            <ListItem>
              <ListIcon as={InfoIcon} color="blue.500" />
              Your settings stay on your computer (not on our servers)
            </ListItem>
            <ListItem>
              <ListIcon as={InfoIcon} color="blue.500" />
              Your browser keeps everything safe
            </ListItem>
            <ListItem>
              <ListIcon as={InfoIcon} color="blue.500" />
              You can clear this data anytime if you want
            </ListItem>
          </List>
          <Text
            mt={3}
            p={3}
            bg="blue.50"
            borderRadius="md"
            borderLeft="4px solid"
            borderLeftColor="blue.400"
          >
            <strong>
              For teams that clear browser history regularly:
            </strong>{' '}
            No problem! Your important features (like AI
            Classification) are tied to your user account, not to
            saved settings. Clear away - your features will still be
            there!
          </Text>
        </Box>

        {/* Your Rights */}
        <Box>
          <Heading size="md" mb={3}>
            5. You're In Control
          </Heading>
          <Text mb={3}>You can always:</Text>
          <List spacing={2} ml={4}>
            <ListItem>
              <ListIcon as={InfoIcon} color="blue.500" />
              Change your settings anytime in the Settings page
            </ListItem>
            <ListItem>
              <ListIcon as={InfoIcon} color="blue.500" />
              Clear your browser data whenever you want
            </ListItem>
            <ListItem>
              <ListIcon as={InfoIcon} color="blue.500" />
              Turn features on or off as needed
            </ListItem>
            <ListItem>
              <ListIcon as={InfoIcon} color="blue.500" />
              Ask us questions about your data
            </ListItem>
          </List>
          <Text
            mt={3}
            p={2}
            bg="gray.50"
            borderRadius="md"
            fontSize="sm"
          >
            <strong>No popups or permissions needed.</strong>{' '}
            Everything just works automatically!
          </Text>
        </Box>

        {/* Contact Information */}
        <Box>
          <Heading size="md" mb={3}>
            6. Contact Us
          </Heading>
          <Text>
            If you have questions about this Privacy Policy or our
            data practices, please contact us at:
          </Text>
          <Box mt={3} p={4} bg="gray.50" borderRadius="md">
            <Text>
              <strong>Denim Health</strong>
              <br />
              Email: support@denimhealth.com
              <br />
            </Text>
          </Box>
        </Box>

        <Divider />

        {/* Footer */}
        <Box textAlign="center" color="gray.600">
          <Text fontSize="sm">
            This privacy policy is effective as of{' '}
            {new Date().toLocaleDateString()} and may be updated
            periodically.
          </Text>
        </Box>
      </VStack>
    </Box>
  );
};

export default PrivacyPolicyPage;
